// The Settings disclosure pattern: a vyv-card whose header row is always
// visible (label + a one-line summary of the CURRENT value) and whose controls
// stay collapsed until tapped.
//
// Why it exists: the Mirror personalization stack rendered every option of
// every section permanently open — three tone cards with previews, a language
// grid, five focus chips, three memory toggles — ~1200px of controls to scroll
// past to reach Notifications, when all a returning user needs is to see what
// is set. The closed row IS the answer ("Idioma do Espelho — Español"); the
// open state is for changing it.
//
// Deliberately NOT an accordion: each card owns its own open state, so opening
// one never closes another (Radix Collapsible per card, not Accordion).
// Radix wires the a11y contract — the trigger is a real <button> with
// aria-expanded/aria-controls, so Enter/Space work natively and screen readers
// announce the state. The whole header row is the target (≥52px tall).

import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface SettingsCollapsibleCardProps {
  title: string;
  /**
   * One-line summary of the current value, shown on the CLOSED row
   * (e.g. "Español", "3 áreas", "22:00–08:00"). Omitted while the value is
   * still loading — the row then shows the title alone, never a made-up state.
   */
  summary?: string;
  defaultOpen?: boolean;
  forceOpen?: boolean;
  children: ReactNode;
}

const SettingsCollapsibleCard = ({
  title,
  summary,
  defaultOpen = false,
  forceOpen = false,
  children,
}: SettingsCollapsibleCardProps) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={forceOpen || open} onOpenChange={setOpen} className="vyv-card">
      <CollapsibleTrigger
        className="w-full min-h-[52px] flex items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-foreground/[0.03] active:bg-foreground/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset rounded-2xl"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-display font-semibold text-foreground">
            {title}
          </p>
          {summary && !open && !forceOpen && (
            <p className="text-[13px] font-display text-foreground/75 mt-1 truncate">
              {summary}
            </p>
          )}
        </div>
        <ChevronDown
          size={15}
          className={`text-muted-foreground shrink-0 transition-transform duration-200 ${
            (open || forceOpen) ? "rotate-180" : ""
          }`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <div className="px-5 pb-5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default SettingsCollapsibleCard;
